/*
  Attar de Astana · About page

  Two moments and nothing else.

  1. Hero entrance. The section's own inline script arms it before first paint; this
     file only replays it when the theme editor reloads the section, and settles it
     when a merchant clicks into the section.
  2. Handover. As the two frames travel up the viewport a 0 to 1 progress is written to
     --handover on the section, and the stylesheet turns it into two transforms. Halfway
     through, the moment locks with is-passed: the headline rises, the frames finish
     their move on a short transition, and the listeners are dropped. A handover does
     not un-happen on the way back up. Locking any later leaves a reader who stops with
     both portraits on screen looking at a section with no headline.

  Nothing here hides content. The pre-animation state exists only after is-armed, and
  reduced motion, a missing IntersectionObserver, or an editor selection all resolve
  straight to the final state.
*/

(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  /* ---------------------------------------------------------------- hero */

  const armHero = (hero) => {
    hero.classList.remove('is-in', 'is-armed');
    if (reduced.matches) return;
    hero.classList.add('is-armed');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => hero.classList.add('is-in'));
    });
  };

  const settleHero = (hero) => {
    if (hero.classList.contains('is-armed')) hero.classList.add('is-in');
  };

  /* ------------------------------------------------------------ handover */

  const LOCK_AT = 0.5;
  const live = new Map();

  const pass = (section) => {
    section.style.setProperty('--handover', '1');
    section.classList.add('is-passed');
    const state = live.get(section);
    if (state) {
      state.stop();
      live.delete(section);
    }
  };

  const initHandover = (section) => {
    if (!section || live.has(section) || section.classList.contains('is-passed')) return;
    if (reduced.matches || !('IntersectionObserver' in window)) {
      pass(section);
      return;
    }

    const frames = section.querySelector('[data-handover-frames]') || section;
    section.classList.add('is-armed');

    let inView = false;
    let queued = false;

    const update = () => {
      queued = false;
      const rect = frames.getBoundingClientRect();
      const viewport = window.innerHeight;
      /*
        0 when the frames' top edge is at the bottom of the viewport, 1 when their
        centre has reached just above the middle of it. Rigid, so a user who stops
        halfway sees a frame halfway up, and one who scrolls on sees it land.
      */
      const from = viewport;
      const to = viewport * 0.55 - rect.height * 0.5;
      const progress = clamp((from - rect.top) / Math.max(from - to, 1), 0, 1);
      section.style.setProperty('--handover', progress.toFixed(3));
      if (progress >= LOCK_AT) pass(section);
    };

    const schedule = () => {
      if (!inView || queued) return;
      queued = true;
      requestAnimationFrame(update);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          inView = entry.isIntersecting;
          if (inView) schedule();
        });
      },
      { rootMargin: '15% 0px 15% 0px', threshold: 0 }
    );

    observer.observe(section);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    live.set(section, {
      stop() {
        observer.disconnect();
        window.removeEventListener('scroll', schedule);
        window.removeEventListener('resize', schedule);
      },
    });

    /* a deep link or a reload can land with the frames already on screen, or past */
    inView = true;
    update();
  };

  /* ---------------------------------------------------------------- wiring */

  const heroes = (root) => root.querySelectorAll('[data-about-hero]');
  const handovers = (root) => root.querySelectorAll('[data-about-handover]');

  const settle = (root) => {
    heroes(root).forEach(settleHero);
    handovers(root).forEach(pass);
  };

  handovers(document).forEach(initHandover);

  reduced.addEventListener('change', () => {
    if (!reduced.matches) return;
    heroes(document).forEach((hero) => hero.classList.remove('is-armed', 'is-in'));
    handovers(document).forEach(pass);
  });

  /* theme editor */

  document.addEventListener('shopify:section:load', (event) => {
    heroes(event.target).forEach(armHero);
    handovers(event.target).forEach(initHandover);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    handovers(event.target).forEach((section) => {
      const state = live.get(section);
      if (state) {
        state.stop();
        live.delete(section);
      }
    });
  });

  document.addEventListener('shopify:section:select', (event) => settle(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const section = event.target.closest('.shopify-section');
    if (section) settle(section);
  });
})();
